goog.declareModuleId('yext.m4.confcode.adminconsole.VariablesModal');

/**
 * @fileoverview The dialog prompt for variables.
 *
 * @author Aditya Saurabh (asaurabh@yext.com)
 */

import {Checkbox} from '/ui/components/checkbox/checkbox';
import {Input} from '/ui/components/input/input';
import {SmallDialog} from '/ui/components/modal/dialog';
import {yext} from '/ui/lib/msg';

/**
 * @typedef {{
 *   name: string,
 *   value: string,
 * }}
 */
let Variable;

/**
 * @typedef {Map<string, VariableType>}
 */
let VariablesMetadata;

/**
 * @typedef {{
 *   displayName: string,
 *   toolTip: string,
 *   type: string,
 * }}
 */
let VariableType;

/**
 * @param {{
 *   variablesMetadata: !VariablesMetadata,
 *   validVariables: !Array<!Variable>,
 *   businessId: number,
 *   invalidVariableNames: !Array<string>,
 *   submit: function(number, !Array<!Variable>),
 *   nextAction: function(),
 * }} props
 * @return {React.ReactNode}
 */
export function VariablesModal(props) {
  const {
    variablesMetadata,
    validVariables,
    businessId,
    invalidVariableNames,
    submit,
    nextAction,
  } = props;

  const [showDialog, setShowDialog] = React.useState(true);
  const [variables, setVariables] = React.useState(validVariables);
  const [error, setError] = React.useState('');

  // When prop refs are changed, it implies that the modal has been invoked again with a new set
  // of props. In this case refresh the states.
  React.useEffect(() => {
    setShowDialog(true);
    setVariables(props.validVariables);
    setError('');
  }, [props]);

  // Handles form submit, closes the dialog and go to next action or show error message.
  const onSubmit =
    () => {
      const {shouldClose, errorMessage} = submit(businessId, variables);
      if (shouldClose) {
        setShowDialog(false);
        nextAction();
      } else {
        setError(errorMessage);
      }
    };

  const buttons = _variableInputButtons(
    validVariables,
    invalidVariableNames,
    onSubmit);

  // Finds variable in variables array and update its value without modifying the array.
  // Generate a new array to avoid side effects.
  const variableInputHandler =
    (name, value) => {
      setVariables(
        variables => variables.map(el => (el['name'] === name ? {...el, value: value} : el)),
      );
    };

  return <SmallDialog
    title={yext.msg('Variables')}
    show={showDialog}
    onHideDialog={() => setShowDialog(false)}
    callToAction={buttons}
  >
    <VariableInputList
      variablesMetadata={variablesMetadata}
      variables={variables}
      invalidNames={invalidVariableNames}
      variableInputHandler={variableInputHandler}/>
    <p style={{color: 'red'}}>{error}</p>
  </SmallDialog>;
}

/**
 * Returns the buttons callToAction objects on the bottom of dialog.
 *
 * @param {!Array<!Variable>} validVariables
 * @param {!Array<string>} invalidVariableNames
 * @param {function()} onSubmit
 * @return {!Object}
 */
function _variableInputButtons(validVariables, invalidVariableNames, onSubmit) {
  if (validVariables.length === 0 || invalidVariableNames.length !== 0) {
    return (
      {
        primary: {
          type: 'default',
          text: yext.msg('Dismiss'),
        },
      }
    );
  }
  return (
    {
      primary: {
        type: 'active',
        text: yext.msg('Save'),
        tid: 'sl-save-variables-button',
        callback: onSubmit,
      },
      cancel: {
        type: 'cancel',
        text: yext.msg('Cancel'),
      },
    }
  );
}

/**
 * @param {{
 *   variablesMetadata: !VariablesMetadata,
 *   variables: !Array<!Variable>,
 *   invalidNames: !Array<string>,
 *   variableInputHandler: function(string, string),
 * }} props
 * @return {React.ReactNode}
 */
function VariableInputList(props) {
  const {
    variablesMetadata,
    variables,
    invalidNames,
    variableInputHandler,
  } = props;

  if (variables.length === 0) {
    return (
      <p>No variables are present. To make a variable, insert&nbsp;
        <code>{'${{VARIABLE_NAME}}'}</code> in a resource file, where&nbsp;
        <code>VARIABLE_NAME</code> consists of alphanumeric characters and underscores.
      </p>
    );
  }
  if (invalidNames.length !== 0) {
    return (
      <p>The following variable names are invalid: <code>{invalidNames.join(', ')}</code>.&nbsp;
        <code>VARIABLE_NAME</code> should consist of alphanumeric characters and underscores.
      </p>
    );
  }

  const inputItems = variables.map((variable, index) =>
    <VariableInputItem
      variablesMetadata={variablesMetadata}
      variable={variable}
      key={index}
      variableInputHandler={variableInputHandler}/>,
  );
  return (<>{inputItems}</>);
}

/**
 * @param {{
 *   variablesMetadata: !VariablesMetadata,
 *   variable: !Variable,
 *   variableInputHandler: function(string, string),
 * }} props
 * @return {React.ReactNode}
 */
function VariableInputItem(props) {
  const {
    variablesMetadata,
    variable,
    variableInputHandler,
  } = props;

  let type = 'text';
  if (variablesMetadata && variablesMetadata.has(variable['name'])) {
    switch (variablesMetadata.get(variable['name'])['type']) {
      case 'integer':
        type = 'number';
        break;
      case 'number':
        type = 'number';
        break;
      case 'boolean':
        return (
          <Checkbox
            id={variable['name']}
            required={true}
            label={variable['name']}
            checked={variable['value'] === 'true'}
            onFocus={() =>
              variableInputHandler(
                variable['name'],
                variable['value'] === 'true' ? 'false' : 'true')}/>
        );
    }
  }

  return (
    <Input id={variable['name']}
      tid="sl-variable-input"
      required={true}
      label={variable['name']}
      type={type}
      value={variable['value']}
      onChange={value => variableInputHandler(variable['name'], value)}/>
  );
}
